import { useFormContext } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { FormSelectionCardGroupField, lt } from '@mews/framework';
import { DataTestId } from '@mews-commander/enums';
import { ReservationLinkResult } from '@mews-commander/data-access';
import { CreatePaymentPlanFormField, PaymentPlanIntervalKind } from '../types';
import { CreatePaymentPlanFormValues } from '../schema';
import { CreatePaymentPlanDialogIntervalBanner } from '../CreatePaymentPlanDialogIntervalBanner';
import { useIsPaymentPlanIntervalKindEnabled } from '../hooks';
import { CreatePaymentPlanDialogDateSelectField } from './CreatePaymentPlanDialogDateSelectField';

export const CreatePaymentPlanDialogIntervalFields = ({ reservation }: { reservation: ReservationLinkResult }) => {
    const { watch, setValue, getValues } = useFormContext<CreatePaymentPlanFormValues>();
    const selectedPaymentPlanIntervalKind = watch(CreatePaymentPlanFormField.PaymentPlanIntervalKind);
    const isCustomIntervalSelected = selectedPaymentPlanIntervalKind === PaymentPlanIntervalKind.Custom;

    const { isReservationIntervalEnabled, isCustomIntervalEnabled } = useIsPaymentPlanIntervalKindEnabled(reservation);

    const reservationRef = useRef(reservation);

    useEffect(() => {
        if (getValues(CreatePaymentPlanFormField.PaymentPlanIntervalKind) && reservationRef.current === reservation) {
            return;
        }
        // sets the default value only when the field has no value or when the reservation has changed
        setValue(
            CreatePaymentPlanFormField.PaymentPlanIntervalKind,
            isReservationIntervalEnabled ? PaymentPlanIntervalKind.Reservation : PaymentPlanIntervalKind.Custom
        );
        reservationRef.current = reservation;
    }, [isReservationIntervalEnabled, setValue, getValues, reservation]);

    return (
        <>
            <CreatePaymentPlanDialogIntervalBanner reservation={reservation} />
            <FormSelectionCardGroupField
                label={lt('CreatePaymentPlanFormPlanIntervalKindLabel')}
                name={CreatePaymentPlanFormField.PaymentPlanIntervalKind}
                type="radio"
                required
                cardVariant="horizontal"
                options={[
                    {
                        label: lt('CreatePaymentPlanFormReservationIntervalOptionLabel'),
                        description: lt('CreatePaymentPlanFormReservationIntervalOptionDescription'),
                        value: PaymentPlanIntervalKind.Reservation,
                        name: PaymentPlanIntervalKind.Reservation,
                        disabled: !isReservationIntervalEnabled,
                        dataTestId: DataTestId.PaymentPlanIntervalKindOptionReservation,
                    },
                    {
                        label: lt('CreatePaymentPlanFormCustomIntervalOptionLabel'),
                        description: lt('CreatePaymentPlanFormCustomIntervalOptionDescription'),
                        value: PaymentPlanIntervalKind.Custom,
                        name: PaymentPlanIntervalKind.Custom,
                        disabled: !isCustomIntervalEnabled,
                        dataTestId: DataTestId.PaymentPlanIntervalKindOptionCustom,
                    },
                ]}
            />
            {isCustomIntervalSelected && <CreatePaymentPlanDialogDateSelectField reservation={reservation} />}
        </>
    );
};
